<template>
  <div class="dashboard-root">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem; color: #3b82f6;"></i>
      <p>Loading portfolio data...</p>
    </div>

    <template v-else>
      <!-- Global Portfolio Summary -->
      <h2 class="section-title">Global Portfolio Summary</h2>

      <div class="summary-cards">
        <!-- Card 1: Total Global Value -->
        <div class="summary-card">
          <div class="card-content-row">
            <div class="card-left">
              <div class="card-header">
                <span class="card-number">1</span>
                <span class="card-label">Total Global Value</span>
              </div>
              <div class="card-value">₹{{ (totalGlobalValue.inr / 100000).toFixed(1) }} Lakh</div>
              <div class="card-subtitle">/ {{ formatUSD(totalGlobalValue.usd) }}</div>
              <button
                class="card-badge tcs-button"
                :class="{ 'tcs-unlocked': !tcsLocked }"
                :title="tcsTooltip"
                @click="openTcsDialog"
              >
                <i class="pi" :class="tcsLocked ? 'pi-lock' : 'pi-lock-open'"></i>
                <b>TCS {{ tcsLocked ? 'locked' : 'unlocked' }}</b>
              </button>
            </div>
            <div class="card-right">
              <i class="pi pi-chart-line card-icon-large"></i>
            </div>
          </div>
        </div>

        <!-- Card 2: Overall Gain/Loss -->
        <div class="summary-card">
          <div class="card-content-row">
            <div class="card-left">
              <div class="card-header">
                <span class="card-number">2</span>
                <span class="card-label">Overall Gain/Loss</span>
              </div>
              <div class="card-value gain-value">
                {{ overallGainLoss.percentage >= 0 ? '+' : '' }}{{ overallGainLoss.percentage.toFixed(1) }}%
                <i class="pi" :class="overallGainLoss.percentage >= 0 ? 'pi-arrow-up' : 'pi-arrow-down'"></i>
              </div>
              <div class="card-subtitle gain-amount">
                {{ overallGainLoss.amount >= 0 ? '+' : '' }}{{ formatCurrency(overallGainLoss.amount / 100000) }}
              </div>
            </div>
          </div>
        </div>

        <!-- Card 3: LRS Utilization -->
        <div class="summary-card">
          <div class="card-content-row">
            <div class="card-left">
              <div class="card-header">
                <span class="card-number">3</span>
                <span class="card-label">LRS Utilization</span>
              </div>
              <div class="card-value">{{ lrsUtilization.percentage }}%</div>
              <div class="card-subtitle">$250k annual cap</div>
            </div>
            <div class="card-right">
              <div class="lrs-ring">
                <svg viewBox="0 0 100 100" class="ring-svg">
                  <circle cx="50" cy="50" r="40" class="ring-bg"></circle>
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    class="ring-progress"
                    :style="{
                      strokeDasharray: `${lrsUtilization.percentage * 2.51} 251`,
                      transform: 'rotate(-90deg)',
                      transformOrigin: '50% 50%'
                    }"
                  ></circle>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Card 4: Forex Rate -->
        <div class="summary-card">
          <div class="card-content-row">
            <div class="card-left">
              <div class="card-header">
                <span class="card-number">4</span>
                <span class="card-label">Forex (USD/INR)</span>
              </div>
              <div class="card-value">₹{{ usdInrRate.toFixed(2) }}</div>
            </div>
            <div class="card-right">
              <div class="mini-chart">
                <svg viewBox="0 0 80 40" class="chart-svg">
                  <polyline
                    points="0,30 10,25 20,28 30,20 40,22 50,15 60,18 70,12 80,10"
                    fill="none"
                    stroke="#06b6d4"
                    stroke-width="2"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Geographical Footprint & Active Alarms - Tabbed -->
      <div class="geo-alarms-container">
        <div class="geo-alarms-card">
          <!-- Tab Headers -->
          <div class="tab-headers">
            <button
              class="tab-header"
              :class="{ active: activeGeoAlarmsTab === 0 }"
              @click="activeGeoAlarmsTab = 0"
            >
              <i class="pi pi-map-marker"></i>
              <span>Geographical Footprint</span>
            </button>
            <button
              class="tab-header"
              :class="{ active: activeGeoAlarmsTab === 1 }"
              @click="activeGeoAlarmsTab = 1"
            >
              <i class="pi pi-bell"></i>
              <span>Active Alarms</span>
              <span v-if="dashboardAlarms.filter(a => a.type !== 'success').length > 0" class="alarm-badge">
                {{ dashboardAlarms.filter(a => a.type !== 'success').length }}
              </span>
            </button>
          </div>

          <!-- Tab Content -->
          <div class="tab-content">
            <!-- Geographical Footprint Tab -->
            <div v-show="activeGeoAlarmsTab === 0" class="tab-panel">
              <div class="geo-visual">
                <v-chart v-if="isMapLoaded && geoChartData.length > 0" class="echarts-map" :option="mapOptions" autoresize />
                <div v-else class="map-placeholder">
                  <i class="pi pi-map-marker" style="font-size: 3rem; color: #475569; opacity: 0.3;"></i>
                  <p style="color: #94a3b8; margin-top: 10px;">{{ isMapLoaded ? 'No geographical data available' : 'Loading map...' }}</p>
                </div>
              </div>

              <!-- Allocation Table Below Map -->
              <div class="allocation-table-container">
                <h4 class="allocation-table-title">ALLOCATION</h4>
                <table class="allocation-table">
                  <thead>
                    <tr>
                      <th v-for="item in geographicalAllocation" :key="item.region" class="allocation-header">
                        {{ item.region }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td v-for="item in geographicalAllocation" :key="item.region" class="allocation-percentage">
                        {{ item.percentage }}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <Button
                label="ADD NEW HOLDING"
                icon="pi pi-plus"
                class="add-holding-btn"
                @click="openAddHoldingDialog"
              />
            </div>

            <!-- Active Alarms Tab -->
            <div v-show="activeGeoAlarmsTab === 1" class="tab-panel">
              <div class="alarms-list">
                <div
                  v-for="(alarm, index) in dashboardAlarms"
                  :key="index"
                  class="alarm-item"
                  :class="alarm.type"
                >
                  <i class="pi" :class="alarm.icon"></i>
                  <div class="alarm-content">
                    <div class="alarm-title">{{ alarm.title }}</div>
                    <div class="alarm-message">{{ alarm.msg }}</div>
                  </div>
                </div>
              </div>

              <!-- Sector Allocation Table -->
              <div class="sector-chart">
                <h4 class="allocation-title">Asset Allocation by Sector</h4>

                <table class="sector-table">
                  <thead>
                    <tr>
                      <th class="sector-col">Sector</th>
                      <th class="funds-col">Funds (ETFs/MFs)<br><span class="col-subtitle">Top 5 holdings</span></th>
                      <th class="stocks-col">Stocks/UDRs</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="sector in allSectors" :key="sector">
                      <td class="sector-name">
                        <i class="pi pi-circle-fill sector-icon" :style="{ color: sectorColors[sector] || sectorColors['Other'] }"></i>
                        {{ sector }}
                      </td>
                      <td class="sector-percentage">
                        {{ getFundsSectorPercentage(sector) }}%
                      </td>
                      <td class="sector-percentage">
                        {{ getStocksSectorPercentage(sector) }}%
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Global Investment Vehicles Table -->
      <div class="vehicles-section">
        <h3 class="section-title">Global Investment Vehicles</h3>
        <DataTable
          :value="investmentVehicles"
          :paginator="false"
          class="vehicles-table"
          responsiveLayout="scroll"
          dataKey="id"
        >
          <Column field="name" header="Vehicle" sortable style="min-width: 200px">
            <template #body="{ data }">
              <div class="vehicle-name">
                <span class="name-text">{{ data.name }}</span>
                <span class="route-badge-inline" :class="data.route.toLowerCase()">{{ data.route }}</span>
              </div>
            </template>
          </Column>
          <Column field="avgCost" header="Avg. Cost" sortable style="min-width: 90px">
            <template #body="{ data }">
              {{ data.currency === 'USD' ? '$' : '₹' }}{{ data.avgCost.toFixed(2) }}
            </template>
          </Column>
          <Column field="currentPrice" header="Current Price" sortable style="min-width: 100px">
            <template #body="{ data }">
              {{ data.currency === 'USD' ? '$' : '₹' }}{{ data.currentPrice.toFixed(2) }}
            </template>
          </Column>
          <Column field="currentValueInr" header="Value (INR)" sortable style="min-width: 90px">
            <template #body="{ data }">
              {{ formatCurrency(data.currentValueInr) }}
            </template>
          </Column>
          <Column field="gainPct" header="Gain (%)" sortable style="min-width: 80px">
            <template #body="{ data }">
              <span :class="['gain-text', data.gainPct >= 0 ? 'positive' : 'negative']">
                {{ data.gainPct >= 0 ? '+' : '' }}{{ data.gainPct.toFixed(1) }}%
              </span>
            </template>
          </Column>
          <Column field="daysChange" header="Day (%)" sortable style="min-width: 80px">
            <template #body="{ data }">
              <span :class="['gain-text', data.daysChange >= 0 ? 'positive' : 'negative']">
                {{ data.daysChange >= 0 ? '+' : '' }}{{ data.daysChange.toFixed(2) }}%
              </span>
            </template>
          </Column>
          <Column header="Actions" style="width: 80px">
            <template #body="{ data }">
              <div class="action-buttons">
                <Button icon="pi pi-cog" class="p-button-text p-button-sm action-btn" />
                <Button
                  icon="pi pi-trash"
                  class="p-button-text p-button-sm p-button-danger action-btn"
                  @click="deleteHolding(data.id)"
                />
              </div>
            </template>
          </Column>
        </DataTable>
      </div>
    </template>

    <!-- Add New Holding Dialog -->
    <Dialog
      v-model:visible="showAddHoldingDialog"
      header="Add New Holding"
      :modal="true"
      :style="{ width: '500px', borderRadius: '12px', overflow: 'hidden' }"
      :contentStyle="{ padding: '0' }"
      :pt="{
        root: { style: 'border-radius: 12px; overflow: hidden;' },
        header: { style: 'display: none;' }
      }"
    >
      <template #header>
        <div class="dialog-header-custom">
          <div class="dialog-header-icon">
            <i class="pi pi-plus-circle"></i>
          </div>
          <div class="dialog-header-text">
            <h3>Add New Holding</h3>
            <p>Add a new security to your global portfolio</p>
          </div>
        </div>
      </template>

      <div class="dialog-content-custom">
        <div class="field-custom">
          <label class="field-label">
            <i class="pi pi-search"></i>
            Select Security
          </label>
          <Select
            v-model="selectedSecurity"
            :options="availableSecurities"
            optionLabel="label"
            optionValue="value"
            placeholder="Choose a security"
            class="w-full dropdown-custom"
          />
        </div>

        <div class="field-row">
          <div class="field-custom">
            <label class="field-label">
              <i class="pi pi-chart-bar"></i>
              Quantity
            </label>
            <InputNumber
              v-model="newHolding.quantity"
              :minFractionDigits="2"
              :maxFractionDigits="4"
              class="w-full input-custom"
              placeholder="0.00"
            />
          </div>

          <div class="field-custom">
            <label class="field-label">
              <i class="pi pi-dollar"></i>
              Average Cost Basis
            </label>
            <InputNumber
              v-model="newHolding.avgCostBasis"
              :minFractionDigits="2"
              :maxFractionDigits="4"
              class="w-full input-custom"
              placeholder="0.00"
            />
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer-custom">
          <Button
            label="Cancel"
            icon="pi pi-times"
            class="p-button-text p-button-secondary cancel-btn-custom"
            @click="showAddHoldingDialog = false"
          />
          <Button
            label="Add Holding"
            icon="pi pi-check"
            class="save-btn-custom"
            @click="saveNewHolding"
          />
        </div>
      </template>
    </Dialog>

    <!-- TCS Status Dialog -->
    <Dialog
      v-model:visible="showTcsDialog"
      :modal="true"
      :style="{ width: '450px', borderRadius: '12px', overflow: 'hidden' }"
      :contentStyle="{ padding: '0' }"
      :pt="{
        root: { style: 'border-radius: 12px; overflow: hidden;' },
        header: { style: 'display: none;' }
      }"
    >
      <div class="tcs-dialog-content">
        <div class="tcs-dialog-icon">
          <i class="pi pi-question-circle"></i>
        </div>
        <h3 class="tcs-dialog-title">TCS Status Check</h3>
        <p class="tcs-dialog-question">
          Are your remittances in the current financial year
          <strong>{{ currentFinancialYear }}</strong>
          greater than <strong>₹10,00,000</strong>?
        </p>
        <div class="tcs-dialog-buttons">
          <Button
            label="No"
            icon="pi pi-times"
            class="tcs-no-btn"
            @click="setTcsStatus(false)"
          />
          <Button
            label="Yes"
            icon="pi pi-check"
            class="tcs-yes-btn"
            @click="setTcsStatus(true)"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useUserStore } from '@/store/user-store';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import VChart from 'vue-echarts';
import { use } from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { ScatterChart, EffectScatterChart } from 'echarts/charts';
import { GeoComponent, TooltipComponent, VisualMapComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import worldJson from '@/assets/maps/world.json';

// Register ECharts components
use([CanvasRenderer, ScatterChart, EffectScatterChart, GeoComponent, TooltipComponent, VisualMapComponent]);

const store = useUserStore();

const globalSecuritiesData = ref(null);
const dashboardData = ref(null);
const isLoading = ref(true);
const showAddHoldingDialog = ref(false);
const selectedSecurity = ref(null);
const newHolding = ref({
  quantity: null,
  avgCostBasis: null
});
const isMapLoaded = ref(false);
const activeGeoAlarmsTab = ref(0); // 0 = Geographical Footprint, 1 = Active Alarms
const showTcsDialog = ref(false);
const tcsLocked = ref(true); // Default to locked

// Current Financial Year
const currentFinancialYear = computed(() => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-11

  if (currentMonth >= 3) { // April (3) or later
    return `${currentYear}-${currentYear + 1}`;
  } else {
    return `${currentYear - 1}-${currentYear}`;
  }
});

// TCS Dialog Functions
const openTcsDialog = () => {
  showTcsDialog.value = true;
};

const setTcsStatus = (isLocked) => {
  tcsLocked.value = isLocked;
  showTcsDialog.value = false;
};

const tcsTooltip = computed(() => {
  return tcsLocked.value
    ? 'LRS threshold reached. 20% TCS will apply to further outbound transfers.'
    : 'You may still have remaining balance in your ₹10L TCS tax-free window.';
});

// Fetch data on mount and load world map
onMounted(async () => {
  try {
    isLoading.value = true;

    // Register the world map using the imported JSON
    echarts.registerMap('world', worldJson);
    isMapLoaded.value = true;
    console.log('World map loaded successfully from local assets');

    const [securities, dashboard] = await Promise.all([
      store.getGlobalSecuritiesData(),
      store.getGlobalDashboardData(store.dashboardData.customerId)
    ]);
    globalSecuritiesData.value = securities;
    dashboardData.value = dashboard;
  } catch (error) {
    console.error('Error loading global dashboard data:', error);
  } finally {
    isLoading.value = false;
  }
});

// USD/INR Rate
const usdInrRate = computed(() => {
  const benchmark = globalSecuritiesData.value?.globalSecuritiesData?.benchmarks?.find(
    b => b.benchmarkName === 'USD INR Rate'
  );
  return benchmark?.currentPrice || 83.52;
});

// Total Global Value
const totalGlobalValue = computed(() => {
  if (!dashboardData.value?.customerGlobalHoldings?.holdings) {
    return { inr: 0, usd: 0 };
  }

  const holdings = dashboardData.value.customerGlobalHoldings.holdings;

  let totalInr = 0;
  let totalUsd = 0;

  holdings.forEach(holding => {
    // Try to find security in globalSecuritiesData
    let security = null;
    if (globalSecuritiesData.value?.globalSecuritiesData?.securities) {
      security = globalSecuritiesData.value.globalSecuritiesData.securities.find(
        s => s.securityId === holding.securityId
      );
    }

    const quantity = parseFloat(holding.quantity) || 0;

    if (quantity > 0) {
      const avgCost = parseFloat(holding.avgCostBasis) || 0;
      const currentPrice = security?.marketData?.closingPrice || avgCost;
      const currency = security?.currency || 'USD';

      const value = quantity * currentPrice;

      if (currency === 'USD') {
        totalUsd += value;
        totalInr += value * usdInrRate.value;
      } else {
        totalInr += value;
      }
    }
  });

  return { inr: totalInr, usd: totalUsd };
});

// LRS Utilization
const lrsUtilization = computed(() => {
  if (!dashboardData.value?.customerGlobalHoldings?.holdings) {
    return { utilized: 0, percentage: 0 };
  }

  let utilized = 0;
  const annualCap = 250000; // $250k annual cap

  dashboardData.value.customerGlobalHoldings.holdings.forEach(holding => {
    const route = holding.investRoute || holding.route;
    if (route === 'GIFT' || route === 'DIRECT') {
      const quantity = parseFloat(holding.quantity) || 0;
      const avgCost = parseFloat(holding.avgCostBasis) || 0;
      utilized += quantity * avgCost;
    }
  });

  return {
    utilized,
    percentage: Math.min(100, Math.round((utilized / annualCap) * 100))
  };
});

// Overall Gain/Loss
const overallGainLoss = computed(() => {
  if (!dashboardData.value?.customerGlobalHoldings?.holdings || !globalSecuritiesData.value?.globalSecuritiesData?.securities) {
    return { amount: 0, percentage: 0 };
  }

  let totalCost = 0;
  let totalCurrent = 0;

  dashboardData.value.customerGlobalHoldings.holdings.forEach(holding => {
    const security = globalSecuritiesData.value.globalSecuritiesData.securities.find(
      s => s.securityId === holding.securityId
    );

    const quantity = parseFloat(holding.quantity) || 0;
    const avgCost = parseFloat(holding.avgCostBasis) || 0;

    if (security?.marketData?.closingPrice && quantity > 0) {
      const cost = quantity * avgCost;
      const current = quantity * security.marketData.closingPrice;

      if (security.currency === 'USD') {
        totalCost += cost * usdInrRate.value;
        totalCurrent += current * usdInrRate.value;
      } else {
        totalCost += cost;
        totalCurrent += current;
      }
    }
  });

  const gain = totalCurrent - totalCost;
  const percentage = totalCost > 0 ? (gain / totalCost) * 100 : 0;

  return { amount: gain, percentage };
});

// Investment Vehicles Table Data
const investmentVehicles = computed(() => {
  if (!dashboardData.value?.customerGlobalHoldings?.holdings) {
    return [];
  }

  return dashboardData.value.customerGlobalHoldings.holdings
    .filter(h => parseFloat(h.quantity) > 0)
    .map(holding => {
      // Try to find security data from globalSecuritiesData
      let security = null;
      if (globalSecuritiesData.value?.globalSecuritiesData?.securities) {
        security = globalSecuritiesData.value.globalSecuritiesData.securities.find(
          s => s.securityId === holding.securityId
        );
      }

      // Use holding's embedded data as fallback
      const securityName = security?.securityName || holding.securityName || holding.securityId;
      const route = holding.investRoute || holding.route || 'N/A';
      const currency = security?.currency || 'USD';
      const currentPrice = security?.marketData?.closingPrice || 0;

      const quantity = parseFloat(holding.quantity) || 0;
      const avgCostBasis = parseFloat(holding.avgCostBasis) || 0;

      const currentValue = quantity * currentPrice;
      const currentValueInr = currency === 'USD'
        ? currentValue * usdInrRate.value
        : currentValue;

      const totalCost = quantity * avgCostBasis;
      const totalCostInr = currency === 'USD'
        ? totalCost * usdInrRate.value
        : totalCost;

      const gain = currentValueInr - totalCostInr;
      const gainPct = totalCostInr > 0 ? (gain / totalCostInr) * 100 : 0;

      return {
        id: holding.securityId,
        name: securityName,
        route: route,
        avgCost: avgCostBasis,
        currentPrice: currentPrice,
        currentValueInr: currentValueInr / 100000, // Convert to Lakhs
        gainPct,
        daysChange: 0, // TODO: Calculate from previousClosingPrice
        quantity: quantity,
        currency: currency
      };
    })
    .filter(Boolean);
});

// Geographical Allocation
const geographicalAllocation = computed(() => {
  if (!dashboardData.value?.customerGlobalHoldings?.holdings) {
    return [];
  }

  const regionMap = {};

  dashboardData.value.customerGlobalHoldings.holdings.forEach(holding => {
    const quantity = parseFloat(holding.quantity) || 0;
    if (quantity <= 0) return;

    // Try to find security data
    let security = null;
    if (globalSecuritiesData.value?.globalSecuritiesData?.securities) {
      security = globalSecuritiesData.value.globalSecuritiesData.securities.find(
        s => s.securityId === holding.securityId
      );
    }

    const avgCost = parseFloat(holding.avgCostBasis) || 0;
    const currentPrice = security?.marketData?.closingPrice || avgCost;
    const currency = security?.currency || 'USD';

    const value = quantity * currentPrice;
    let valueInr = currency === 'USD' ? value * usdInrRate.value : value;

    // Get primary region from security data
    const region = security?.primaryRegion || 'Global';

    if (!regionMap[region]) {
      regionMap[region] = 0;
    }
    regionMap[region] += valueInr;
  });

  const total = Object.values(regionMap).reduce((sum, val) => sum + val, 0);

  return Object.entries(regionMap)
    .map(([region, value]) => ({
      region,
      value,
      percentage: total > 0 ? Math.round((value / total) * 100) : 0
    }))
    .sort((a, b) => b.value - a.value);
});

// Helper function to get region coordinates
const getRegionCoordinates = (region) => {
  const coordinates = {
    'US': [-95.7, 37.1],
    'USA': [-95.7, 37.1],
    'Taiwan': [121.0, 23.5],
    'Germany': [10.4515, 51.1657],
    'China': [104.1, 35.8],
    'India': [78.9629, 20.5937],
    'UK': [-3.435973, 55.378051],
    'Japan': [138.2529, 36.2048],
    'Canada': [-106.346771, 56.130366],
    'Australia': [133.7751, -25.2744],
    'Brazil': [-51.9253, -14.2350],
    'Singapore': [103.8198, 1.3521],
    'France': [2.2137, 46.2276],
    'Global': [0.0, 20.0]
  };
  return coordinates[region] || [0.0, 20.0];
};

// ECharts map data
const geoChartData = computed(() => {
  return geographicalAllocation.value.map(item => ({
    name: item.region,
    value: [...getRegionCoordinates(item.region), item.percentage]
  }));
});

// ECharts map options
const mapOptions = computed(() => {
  if (!isMapLoaded.value || geoChartData.value.length === 0) {
    return {};
  }

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        if (params.value) {
          return `<strong>${params.name}</strong><br/>Allocation: ${params.value[2]}%`;
        }
        return params.name;
      }
    },
    geo: {
      map: 'world',
      roam: false,
      itemStyle: {
        areaColor: '#475569',
        borderColor: '#64748b',
        borderWidth: 0.5
      },
      emphasis: {
        disabled: true,
        itemStyle: {
          areaColor: '#475569'
        }
      },
      silent: true
    },
    series: [
      {
        type: 'effectScatter',
        coordinateSystem: 'geo',
        data: geoChartData.value,
        symbolSize: (val) => {
          // Scale based on percentage (min 15, max 60)
          return Math.max(15, Math.min(60, val[2] * 0.8));
        },
        showEffectOn: 'render',
        rippleEffect: {
          brushType: 'stroke',
          scale: 2.5,
          period: 4
        },
        label: {
          show: true,
          formatter: '{@[2]}%',
          position: 'inside',
          color: '#ffffff',
          fontWeight: 'bold',
          fontSize: 12
        },
        itemStyle: {
          color: '#06b6d4',
          shadowBlur: 10,
          shadowColor: 'rgba(6, 182, 212, 0.5)'
        },
        zlevel: 1
      }
    ]
  };
});

// Sector Allocation - Funds Only
const fundsSectorAllocation = computed(() => {
  if (!dashboardData.value?.customerGlobalHoldings?.holdings) {
    return [];
  }

  const sectorMap = {};
  let totalFundsValue = 0;

  // Calculate total funds value and sector distribution
  dashboardData.value.customerGlobalHoldings.holdings.forEach(holding => {
    const quantity = parseFloat(holding.quantity) || 0;
    if (quantity <= 0) return;

    let security = null;
    if (globalSecuritiesData.value?.globalSecuritiesData?.securities) {
      security = globalSecuritiesData.value.globalSecuritiesData.securities.find(
        s => s.securityId === holding.securityId
      );
    }

    const topHoldings = holding.topHoldings || security?.topHoldings;

    // Only process funds (those with topHoldings)
    if (topHoldings && Array.isArray(topHoldings) && topHoldings.length > 0) {
      const avgCost = parseFloat(holding.avgCostBasis) || 0;
      const currentPrice = security?.marketData?.closingPrice || avgCost;
      const currency = security?.currency || 'USD';

      const value = quantity * currentPrice;
      const valueInr = currency === 'USD' ? value * usdInrRate.value : value;

      totalFundsValue += valueInr;

      // Distribute value across sectors based on fund composition
      topHoldings.forEach(th => {
        const sector = th.sector || 'Other';
        const sectorWeight = (th.pct || 0) / 100;
        const sectorValue = valueInr * sectorWeight;

        if (!sectorMap[sector]) {
          sectorMap[sector] = 0;
        }
        sectorMap[sector] += sectorValue;
      });
    }
  });

  // Calculate percentages
  return Object.entries(sectorMap)
    .map(([sector, value]) => ({
      sector,
      value,
      percentage: totalFundsValue > 0 ? Math.round((value / totalFundsValue) * 100) : 0
    }))
    .filter(item => item.percentage > 0)
    .sort((a, b) => b.value - a.value);
});

// Sector Allocation - Stocks/UDRs Only
const stocksSectorAllocation = computed(() => {
  if (!dashboardData.value?.customerGlobalHoldings?.holdings) {
    return [];
  }

  const sectorMap = {};
  let totalStocksValue = 0;

  // Calculate total stocks value and sector distribution
  dashboardData.value.customerGlobalHoldings.holdings.forEach(holding => {
    const quantity = parseFloat(holding.quantity) || 0;
    if (quantity <= 0) return;

    let security = null;
    if (globalSecuritiesData.value?.globalSecuritiesData?.securities) {
      security = globalSecuritiesData.value.globalSecuritiesData.securities.find(
        s => s.securityId === holding.securityId
      );
    }

    const topHoldings = holding.topHoldings || security?.topHoldings;

    // Only process stocks/UDRs (those WITHOUT topHoldings)
    if (!topHoldings || !Array.isArray(topHoldings) || topHoldings.length === 0) {
      const avgCost = parseFloat(holding.avgCostBasis) || 0;
      const currentPrice = security?.marketData?.closingPrice || avgCost;
      const currency = security?.currency || 'USD';

      const value = quantity * currentPrice;
      const valueInr = currency === 'USD' ? value * usdInrRate.value : value;

      totalStocksValue += valueInr;

      const sector = holding.sector || security?.sector || 'Other';

      if (!sectorMap[sector]) {
        sectorMap[sector] = 0;
      }
      sectorMap[sector] += valueInr;
    }
  });

  // Calculate percentages
  return Object.entries(sectorMap)
    .map(([sector, value]) => ({
      sector,
      value,
      percentage: totalStocksValue > 0 ? Math.round((value / totalStocksValue) * 100) : 0
    }))
    .filter(item => item.percentage > 0)
    .sort((a, b) => b.value - a.value);
});

// All unique sectors across both funds and stocks
const allSectors = computed(() => {
  const sectors = new Set();
  fundsSectorAllocation.value.forEach(s => sectors.add(s.sector));
  stocksSectorAllocation.value.forEach(s => sectors.add(s.sector));
  return Array.from(sectors).sort();
});

// Get funds percentage for a specific sector
const getFundsSectorPercentage = (sector) => {
  const fundsItem = fundsSectorAllocation.value.find(s => s.sector === sector);
  return fundsItem?.percentage || 0;
};

// Get stocks percentage for a specific sector
const getStocksSectorPercentage = (sector) => {
  const stocksItem = stocksSectorAllocation.value.find(s => s.sector === sector);
  return stocksItem?.percentage || 0;
};

// Chart colors for sectors
const sectorColors = {
  'Technology': '#3b82f6',
  'Healthcare': '#10b981',
  'Consumer Discretionary': '#f59e0b',
  'Financials': '#8b5cf6',
  'Communication Services': '#06b6d4',
  'Industrials': '#6366f1',
  'Other': '#6b7280'
};

// Dashboard Alarms - Analytics
const dashboardAlarms = computed(() => {
  const alerts = [];

  if (!dashboardData.value?.customerGlobalHoldings?.holdings) {
    return alerts;
  }

  let totalDirectValueUSD = 0;
  const sectorConcentration = {}; // To map sector concentration for stocks/UDRs
  let totalPortfolioValueUSD = 0;

  dashboardData.value.customerGlobalHoldings.holdings.forEach(holding => {
    const quantity = parseFloat(holding.quantity) || 0;
    if (quantity <= 0) return;

    // Try to find security data
    let security = null;
    if (globalSecuritiesData.value?.globalSecuritiesData?.securities) {
      security = globalSecuritiesData.value.globalSecuritiesData.securities.find(
        s => s.securityId === holding.securityId
      );
    }

    const avgCost = parseFloat(holding.avgCostBasis) || 0;
    const currentPrice = security?.marketData?.closingPrice || avgCost;
    const currency = security?.currency || 'USD';

    const currentValue = quantity * currentPrice;
    const currentValueUSD = currency === 'USD' ? currentValue : currentValue / usdInrRate.value;

    totalPortfolioValueUSD += currentValueUSD;

    // Logic for Estate Tax & LRS
    const route = holding.investRoute || holding.route || security?.investRoute;
    if (route === 'DIRECT') {
      totalDirectValueUSD += currentValueUSD;
    }

    // Logic for Sector Concentration - Only for stocks/UDRs (those with sector, not topHoldings)
    const hasTopHoldings = holding.topHoldings && Array.isArray(holding.topHoldings) && holding.topHoldings.length > 0;
    if (!hasTopHoldings) {
      const sector = holding.sector || security?.sector;
      if (sector) {
        sectorConcentration[sector] = (sectorConcentration[sector] || 0) + currentValueUSD;
      }
    }
  });

  // Trigger Alarms
  if (totalDirectValueUSD > 60000) {
    alerts.push({
      type: 'warning',
      icon: 'pi-exclamation-triangle',
      title: 'US Estate Tax Warning',
      msg: `Direct US holdings ($${totalDirectValueUSD.toLocaleString(undefined, { maximumFractionDigits: 0 })}) exceed $60k threshold`
    });
  }

  // Check for 25% concentration in any single sector (for stocks/UDRs only)
  Object.keys(sectorConcentration).forEach(sector => {
    const concentration = sectorConcentration[sector];
    const concentrationPct = totalPortfolioValueUSD > 0 ? (concentration / totalPortfolioValueUSD) * 100 : 0;

    if (concentrationPct > 25) {
      alerts.push({
        type: 'info',
        icon: 'pi-info-circle',
        title: 'High Sector Concentration',
        msg: `${concentrationPct.toFixed(1)}% of portfolio is in ${sector} sector (stocks/UDRs)`
      });
    }
  });

  // If no alerts, show a success message
  if (alerts.length === 0) {
    alerts.push({
      type: 'success',
      icon: 'pi-check-circle',
      title: 'No Active Alarms',
      msg: 'Your portfolio is within all safety thresholds'
    });
  }

  return alerts;
});

// Add New Holding
const openAddHoldingDialog = () => {
  showAddHoldingDialog.value = true;
  selectedSecurity.value = null;
  newHolding.value = { quantity: null, avgCostBasis: null };
};

const availableSecurities = computed(() => {
  return globalSecuritiesData.value?.globalSecuritiesData?.securities?.map(s => ({
    label: s.securityName,
    value: s.securityId
  })) || [];
});

const saveNewHolding = async () => {
  if (!selectedSecurity.value || !newHolding.value.quantity || !newHolding.value.avgCostBasis) {
    return;
  }

  try {
    await store.updateGlobalDashboardData({
      customer_id: store.dashboardData.customerId,
      security_id: selectedSecurity.value,
      quantity: newHolding.value.quantity.toString(),
      avgCostBasis: newHolding.value.avgCostBasis.toString()
    });

    // Refresh data
    dashboardData.value = await store.getGlobalDashboardData({ CustomerId: store.dashboardData.customerId });
    showAddHoldingDialog.value = false;
  } catch (error) {
    console.error('Error adding holding:', error);
  }
};

// Delete Holding
const deleteHolding = async (securityId) => {
  if (!confirm('Are you sure you want to delete this holding?')) return;

  try {
    await store.updateGlobalDashboardData({
      customer_id: store.dashboardData.customerId,
      security_id: securityId,
      quantity: "0"
    });

    // Refresh data
    dashboardData.value = await store.getGlobalDashboardData({ CustomerId: store.dashboardData.customerId });
  } catch (error) {
    console.error('Error deleting holding:', error);
  }
};

const formatCurrency = (value) => {
  return `₹${value.toFixed(2)}L`;
};

const formatUSD = (value) => {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
</script>

<style scoped>
.dashboard-root {
  padding: 20px;
  background: #ffffff;
  min-height: 100vh;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  color: #6c757d;
}

.section-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 16px 0;
  background: #1e293b;
  padding: 12px 16px;
  border-radius: 8px;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.summary-card {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  border-radius: 8px;
  padding: 10px 12px;
  color: white;
  position: relative;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  min-height: 85px;
}

.card-content-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  height: 100%;
}

.card-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.card-right {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 5px;
  opacity: 0.9;
  margin-bottom: 2px;
}

.card-number {
  width: 16px;
  height: 16px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.6rem;
}

.card-label {
  font-size: 0.65rem;
  font-weight: 500;
  flex: 1;
}

.card-icon-large {
  font-size: 2rem;
  opacity: 0.3;
  color: #06b6d4;
}

.card-value {
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 1px;
  line-height: 1.1;
}

.card-subtitle {
  font-size: 0.65rem;
  opacity: 0.7;
}

.card-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  background: rgba(251, 191, 36, 0.9);
  padding: 1px 5px;
  border-radius: 8px;
  font-size: 0.55rem;
  margin-top: 2px;
  width: fit-content;
  color: #78350f;
}

.tcs-button {
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tcs-button:hover {
  background: rgba(251, 191, 36, 1);
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.4);
}

.tcs-button.tcs-unlocked {
  background: rgba(16, 185, 129, 0.9);
  color: #064e3b;
}

.tcs-button.tcs-unlocked:hover {
  background: rgba(16, 185, 129, 1);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
}

/* TCS Dialog Styles */
.tcs-dialog-content {
  padding: 32px 24px;
  text-align: center;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.tcs-dialog-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(6, 182, 212, 0.3);
}

.tcs-dialog-icon i {
  font-size: 2rem;
  color: #ffffff;
}

.tcs-dialog-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 16px 0;
}

.tcs-dialog-question {
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.6;
  margin: 0 0 24px 0;
}

.tcs-dialog-question strong {
  color: #1e293b;
  font-weight: 700;
}

.tcs-dialog-buttons {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.tcs-no-btn {
  background: #64748b;
  border: none;
  color: #ffffff;
  font-weight: 600;
  padding: 10px 32px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.tcs-no-btn:hover {
  background: #475569;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(100, 116, 139, 0.3);
}

.tcs-yes-btn {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  border: none;
  color: #ffffff;
  font-weight: 600;
  padding: 10px 32px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.tcs-yes-btn:hover {
  background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(6, 182, 212, 0.4);
}

.gain-value {
  color: #10b981;
  display: flex;
  align-items: center;
  gap: 6px;
}

.gain-amount {
  color: #10b981;
  font-weight: 600;
}

/* LRS Ring Chart */
.lrs-ring {
  position: relative;
  width: 55px;
  height: 55px;
}

.ring-svg {
  width: 100%;
  height: 100%;
}

.ring-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.2);
  stroke-width: 4;
}

.ring-progress {
  fill: none;
  stroke: #06b6d4;
  stroke-width: 4;
  stroke-linecap: round;
  transition: stroke-dasharray 0.5s ease;
}

.mini-chart {
  width: 65px;
  height: 35px;
}

.chart-svg {
  width: 100%;
  height: 100%;
}

/* Geographical & Alarms - Tabbed Container */
.geo-alarms-container {
  margin-bottom: 24px;
}

.geo-alarms-card {
  background: #1e293b;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

/* Tab Headers */
.tab-headers {
  display: flex;
  background: #0f172a;
  border-bottom: 2px solid #334155;
}

.tab-header {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 20px;
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.tab-header i {
  font-size: 1.1rem;
}

.tab-header:hover {
  background: rgba(6, 182, 212, 0.1);
  color: #06b6d4;
}

.tab-header.active {
  color: #06b6d4;
  background: rgba(6, 182, 212, 0.15);
}

.tab-header.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  right: 0;
  height: 2px;
  background: #06b6d4;
}

.alarm-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: #ef4444;
  color: #ffffff;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  margin-left: 4px;
}

/* Tab Content */
.tab-content {
  background: #1e293b;
}

.tab-panel {
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Old geo-alarms-row styles - keep for backward compatibility but update */
.geo-alarms-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.geo-card,
.alarms-card {
  background: #1e293b;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.card-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 16px 0;
}

.geo-visual {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  background-image:
    radial-gradient(circle at 20% 30%, rgba(6, 182, 212, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(6, 182, 212, 0.08) 0%, transparent 50%);
  border-radius: 8px;
  padding: 1px;
  overflow: hidden;
  margin-bottom: 1px;
}

.echarts-map {
  width: 150%;
  height: 400px;
}

.map-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 400px;
  width: 100%;
}

.geo-icon {
  font-size: 4rem;
  color: #475569;
  opacity: 0.3;
}

.allocation-table-container {
  margin: 8px 0;
  padding: 8px 4px;
  background: rgba(15, 23, 42, 0.5);
  border-radius: 6px;
}

.allocation-table-title {
  font-size: 0.65rem;
  font-weight: 600;
  color: #06b6d4;
  margin: 0 0 2px 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.allocation-table {
  width: 100%;
  border-collapse: collapse;
}

.allocation-header {
  padding: 4px 2px;
  text-align: center;
  font-weight: 600;
  font-size: 0.65rem;
  color: #94a3b8;
  border-bottom: 1px solid #334155;
  text-transform: uppercase;
  letter-spacing: 0.2px;
}

.allocation-percentage {
  padding: 4px 2px;
  text-align: center;
  font-weight: 700;
  font-size: 0.95rem;
  color: #ffffff;
}

.add-holding-btn {
  width: 100%;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  border: none;
  color: #78350f;
  font-weight: 700;
  padding: 12px;
  border-radius: 8px;
}

/* Active Alarms */
.alarms-list {
  margin-bottom: 24px;
}

.allocation-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #06b6d4;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.alarm-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 8px;
  font-size: 0.9rem;
  font-weight: 500;
}

.alarm-item i {
  font-size: 1.1rem;
  margin-top: 2px;
  flex-shrink: 0;
}

.alarm-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.alarm-title {
  font-weight: 700;
  font-size: 0.95rem;
}

.alarm-message {
  font-size: 0.85rem;
  opacity: 0.9;
  line-height: 1.4;
}

.alarm-item.warning {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #78350f;
}

.alarm-item.error {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #7f1d1d;
}

.alarm-item.info {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e3a8a;
}

.alarm-item.success {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #065f46;
}

/* Sector Chart */
.sector-chart {
  padding-top: 20px;
  border-top: 1px solid #334155;
}

/* Sector Table */
.sector-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
}

.sector-table thead tr {
  background: #334155;
}

.sector-table th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 600;
  font-size: 0.8rem;
  color: #ffffff;
  border-bottom: 2px solid #475569;
  line-height: 1.3;
}

.sector-table th.sector-col {
  width: 40%;
}

.sector-table th.funds-col,
.sector-table th.stocks-col {
  width: 30%;
  text-align: center;
}

.col-subtitle {
  font-size: 0.7rem;
  font-weight: 400;
  color: #94a3b8;
  font-style: italic;
}

.sector-table tbody tr {
  border-bottom: 1px solid #334155;
}

.sector-table tbody tr:hover {
  background: #2a3c50;
}

.sector-table td {
  padding: 12px 16px;
  color: #cbd5e1;
  font-size: 0.9rem;
}

.sector-name {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
}

.sector-icon {
  font-size: 0.7rem;
}

.sector-percentage {
  text-align: center;
  font-weight: 700;
  color: #ffffff;
  font-size: 1rem;
}

/* Vehicles Table */
.vehicles-section {
  background: #1e293b;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.vehicles-table {
  margin-top: 16px;
}

/* PrimeVue DataTable Overrides */
.vehicles-table :deep(.p-datatable-header) {
  background: transparent;
  border: none;
}

.vehicles-table :deep(.p-datatable-thead > tr > th) {
  background: #0d9488;
  color: #ffffff;
  border: none;
  padding: 8px 6px;
  font-weight: 600;
  font-size: 0.75rem;
  white-space: nowrap;
  vertical-align: middle;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.vehicles-table :deep(.p-datatable-tbody > tr) {
  background: #1e293b;
  color: #cbd5e1;
}

.vehicles-table :deep(.p-datatable-tbody > tr:hover) {
  background: #2a3c50 !important;
}

.vehicles-table :deep(.p-datatable-tbody > tr > td) {
  border: none;
  border-bottom: 1px solid #334155;
  padding: 8px 6px;
  color: #cbd5e1;
  font-size: 0.85rem;
}

.vehicles-table :deep(.p-checkbox .p-checkbox-box) {
  background: #334155;
  border-color: #475569;
}

.vehicles-table :deep(.p-checkbox .p-checkbox-box.p-highlight) {
  background: #0d9488;
  border-color: #0d9488;
}

.vehicle-name {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.name-text {
  font-weight: 600;
  color: #ffffff;
  font-size: 0.85rem;
}

.route-badge-inline {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
}

.route-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  width: fit-content;
}

.route-badge.gift,
.route-badge-inline.gift {
  background: #1e40af;
  color: #dbeafe;
}

.route-badge.feeder,
.route-badge-inline.feeder {
  background: #6b21a8;
  color: #f3e8ff;
}

.route-badge.direct,
.route-badge-inline.direct {
  background: #92400e;
  color: #fed7aa;
}

.gain-text.positive {
  color: #10b981;
  font-weight: 600;
}

.gain-text.negative {
  color: #ef4444;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  gap: 4px;
}

/* Dialog */
.dialog-header-custom {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border-radius: 12px 12px 0 0;
}

.dialog-header-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(6, 182, 212, 0.3);
}

.dialog-header-text h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
}

.dialog-header-text p {
  margin: 4px 0 0 0;
  font-size: 0.85rem;
  color: #94a3b8;
}

.dialog-content-custom {
  padding: 24px;
  background: #f8fafc;
}

.field-custom {
  margin-bottom: 20px;
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.field-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #1e293b;
  font-size: 0.9rem;
  margin-bottom: 8px;
}

.field-label i {
  color: #06b6d4;
  font-size: 0.9rem;
}

.dialog-footer-custom {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  border-radius: 0 0 12px 12px;
}

.cancel-btn-custom {
  color: #64748b;
  font-weight: 600;
}

.cancel-btn-custom:hover {
  background: #f1f5f9 !important;
  color: #475569 !important;
}

.save-btn-custom {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  border: none;
  color: #ffffff;
  font-weight: 700;
  padding: 10px 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(6, 182, 212, 0.3);
  transition: all 0.3s ease;
}

.save-btn-custom:hover {
  background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
  box-shadow: 0 4px 12px rgba(6, 182, 212, 0.4);
  transform: translateY(-1px);
}

.dropdown-custom,
.input-custom {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.dropdown-custom:hover,
.input-custom:hover {
  border-color: #06b6d4;
}

/* PrimeVue InputNumber Styling */
.input-custom :deep(.p-inputnumber-input) {
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #1e293b;
  background: #ffffff;
  transition: all 0.3s ease;
  width: 100%;
}

.input-custom :deep(.p-inputnumber-input:hover) {
  border-color: #06b6d4;
  background: #f0fdfa;
}

.input-custom :deep(.p-inputnumber-input:focus) {
  outline: none;
  border-color: #06b6d4;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.1);
}

.input-custom :deep(.p-inputnumber-input::placeholder) {
  color: #94a3b8;
  font-weight: 400;
}

/* PrimeVue Select (Dropdown) Styling */
.dropdown-custom :deep(.p-select) {
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.3s ease;
  background: #ffffff;
}

.dropdown-custom :deep(.p-select:hover) {
  border-color: #06b6d4;
  background: #f0fdfa;
}

.dropdown-custom :deep(.p-select.p-focus) {
  outline: none;
  border-color: #06b6d4;
  box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.1);
}

.dropdown-custom :deep(.p-select-label) {
  padding: 12px 16px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #1e293b;
}

.dropdown-custom :deep(.p-select-label.p-placeholder) {
  color: #94a3b8;
  font-weight: 400;
}

.dropdown-custom :deep(.p-select-dropdown) {
  color: #64748b;
}

.w-full {
  width: 100%;
}

.dialog-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px 0;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

@media (max-width: 968px) {
  .geo-alarms-row {
    grid-template-columns: 1fr;
  }

  .summary-cards {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  .summary-cards {
    grid-template-columns: 1fr;
  }

  .geo-content {
    flex-direction: column;
  }
}
</style>
